import React from "react";
import { Box, Typography, IconButton, Divider, Chip, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import UITooltip from "components/ui/Tooltip/UITooltip";
import ProgressBar from "components/loader/LinearProgress";
import dayjs from "dayjs";

const NOTIFICATION_STATUS = {
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  IN_PROGRESS: "IN_PROGRESS",
  QUEUED: "QUEUED",
};

const formatNotificationTimestamp = (timestamp) => {
  const now = dayjs();
  const notificationDate = dayjs(timestamp);

  if (notificationDate.isSame(now, "day")) {
    const diffMins = now.diff(notificationDate, "minute");
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = now.diff(notificationDate, "hour");
    return `${diffHours}h ago`;
  }
  return notificationDate.format("MMM D");
};

const getStatusProps = (status, isDarkTheme) => {
  switch (status) {
    case NOTIFICATION_STATUS.COMPLETED:
      return {
        icon: <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: "20px" }} />,
        color: "#48BA7814",
        hoverColor: "#48BA7824",
        borderColor: "#48BA78",
      };
    case NOTIFICATION_STATUS.FAILED:
      return {
        icon: <ErrorIcon sx={{ color: "#f44336", fontSize: "20px" }} />,
        color: isDarkTheme ? "#DE302414" : "#DE30240F",
        hoverColor: isDarkTheme ? "#DE302424" : "#DE30241F",
        borderColor: "#DE3024",
      };
    case NOTIFICATION_STATUS.IN_PROGRESS:
    case NOTIFICATION_STATUS.QUEUED:
      return {
        icon: null,
        color: "#5D5FEF14",
        hoverColor: "#5D5FEF14",
        borderColor: "#5D5FEF",
      };
    default:
      return {
        icon: null,
        color: isDarkTheme ? "#333" : "#f0f0f0",
        borderColor: "grey.500",
        hoverColor: isDarkTheme ? "#444" : "#e0e0e0",
      };
  }
};

const BackgroundTaskFooter = ({ notification, isDarkTheme, i18n, onRetry }) => {
  const FooterText = ({ children }) => (
    <Typography fontSize={11} sx={{ fontWeight: "400", flex: 1 }}>
      {children}
    </Typography>
  );

  const Timestamp = () => (
    <Typography
      fontSize={9}
      sx={{ fontWeight: "400", color: "text.secondary" }}
    >
      {formatNotificationTimestamp(notification.addedOn)}
    </Typography>
  );

  const soapNoteTask = notification?.taskGroups?.find(item => item.code === "task_group@notes.soapnote.generate");
  const ERROR = notification?.error === "please provide medical-related conversation"

  switch (notification.status) {
    case NOTIFICATION_STATUS.COMPLETED:
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          <FooterText>
            <Typography
              component="span"
              fontSize={12}
              sx={{
                fontWeight: "500",
                color: isDarkTheme ? "#a9a9ff" : "primary.main",
                ml: 4.5,
              }}
            >
              {notification.task_category === "template"
                ? i18n.t("notifications.VIEW_TEMPLATE")
                : i18n.t("notifications.VIEW_SOAP_NOTE")}
            </Typography>
          </FooterText>
          <Timestamp />
        </Box>
      );
    case NOTIFICATION_STATUS.FAILED:
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          <FooterText>
            {notification?.error && (
              <Typography
                fontSize={12}
                sx={{
                  fontWeight: "500",
                  color: "#f44336",
                  mb: 0.5,
                  ml: 4.5,
                }}
              >
                {notification?.error}
              </Typography>
            )}

            {notification.task_category === "soap" && (
              <Box sx={{ display: "flex", alignItems: "center", ml: 4.5, gap: 1 }}>
                <Typography
                  component="span"
                  fontSize={12}
                  sx={{
                    fontWeight: 500,
                    color: isDarkTheme ? "#a9a9ff" : "primary.main",
                  }}
                >
                  {soapNoteTask?.status === "COMPLETED"
                    ? i18n.t("notifications.VIEW_SOAP_NOTE")
                    : i18n.t("notifications.VIEW_DRAFT_TRANSCRIPT")}
                </Typography>

                {soapNoteTask?.status === "FAILED" && !ERROR && (
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetry(notification);
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: "12px",
                      fontWeight: 500,
                      minWidth: "auto",
                      color: isDarkTheme ? "#a9a9ff" : "primary.main",
                      "&:hover": {
                        backgroundColor: isDarkTheme ? "#5D5FEF14" : "#5D5FEF0A",
                      },
                    }}
                  >
                    {i18n.t("common.RETRY")}
                  </Button>
                )}
              </Box>)}

          </FooterText>
          <Timestamp />
        </Box>
      );
    default:
      return null;
  }
};

const ToastNotification = ({ notification, onDelete, isDarkTheme }) => {
  const { i18n } = useTranslation();
  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
          margin: "2px 12px 8px",
          borderRadius: "8px",
          minHeight: "60px",
          ...(!notification?.isRead && {
            ...(!isDarkTheme && {
              background: "#9e9ff11f",
              border: "1px solid #a2a7b17d",
            }),
            ...(isDarkTheme && {
              background: "#6163f121",
              border: "1px solid #a2a7b17d",
            }),
          }),
          ...(notification?.isRead && {
            background: "inherit",
            border: "1px solid #a2a7b17d",
            opacity: 0.7,
          }),
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={13} sx={{ fontFamily: "Poppins", mb: "4px" }}>
            {notification?.message}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginLeft: "8px",
          }}
        >
          <UITooltip title={i18n.t("common.DELETE")}>
            <IconButton size="small" onClick={() => onDelete(notification)}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </UITooltip>

          <Typography
            fontSize={9}
            sx={{
              fontFamily: "Poppins",
              fontWeight: "400",
              whiteSpace: "nowrap",
            }}
          >
            {formatNotificationTimestamp(notification?.addedOn)}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

const BackgroundTaskNotification = ({
  notification,
  onDelete,
  isDarkTheme,
  handleNotificationClick,
  onRetry,
}) => {
  const { i18n } = useTranslation();
  const statusProps = getStatusProps(notification.status, isDarkTheme);

  const archivedEncounter = useSelector(
    (state) => state?.encounterObj?.selectedArchivedEncounter
  );

  const soapNoteTask = notification?.taskGroups?.find(
    item => item.code === "task_group@notes.soapnote.generate"
  );

  // Check if notification can be retried
  const hasRetryOption =
    notification.status === NOTIFICATION_STATUS.FAILED &&
    notification.task_category === "soap" &&
    soapNoteTask?.status === "FAILED";

  // Disable notification only if it's archived AND doesn't have a retry option
  const isDisabled = Boolean(
    archivedEncounter &&
    notification?.encounterId === archivedEncounter?.id &&
    notification.task_category === 'soap' &&
    !hasRetryOption
  );

  const isClickable =
    !notification?.isRead &&
    !isDisabled &&
    (notification.status === NOTIFICATION_STATUS.COMPLETED ||
      (notification.status === NOTIFICATION_STATUS.FAILED &&
        notification.task_category === "soap"));

  const ERROR = notification?.error === "please provide medical-related conversation"

  return (
    <>
      <Box
        onClick={() => isClickable && handleNotificationClick(notification)}
        sx={{
          padding: "12px",
          margin: "4px 12px 6px",
          borderRadius: "8px",
          background: statusProps.color,
          border: `0.5px solid ${statusProps.borderColor}`,
          transition: "opacity 0.3s ease-in-out, background 0.2s ease-in-out",
          opacity: notification.isRead ? 0.7 : 1,
          ...(isClickable && {
            cursor: "pointer",
            "&:hover": { background: statusProps.hoverColor },
          }),
          ...(isDisabled && {
            border: `0.5px solid rgb(255, 193, 7)`,
            opacity: 0.7,
            filter: "grayscale(40%)",
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {statusProps.icon}
          <Typography
            fontSize={13}
            sx={{ flex: 1, ml: statusProps.icon ? 1 : 0 }}
          >
            {(notification.status === NOTIFICATION_STATUS.IN_PROGRESS ||
              notification.status === NOTIFICATION_STATUS.QUEUED) &&
              notification.patientName ? (
              <>
                {ERROR ? i18n.t("common.NON_MEDICAL_CONVERSATION") : notification.title} for{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, display: "inline" }}
                >
                  {notification.patientName}
                </Box>
              </>
            ) : notification.patientName ? (
              <>
                {isDisabled && (
                  <Typography
                    fontSize={12}
                    sx={{
                      fontWeight: 600,
                      color: "rgb(255, 193, 7)",
                      mb: 0.5,
                    }}
                  >
                    {i18n.t("notifications.ENCOUNTER_ARCHIVED")}
                  </Typography>
                )}

                <div style={{ color: isDarkTheme ? "#A0A4AB" : "#757677ff" }}>
                  {i18n.t("Patient_Screen.PATIENT_NAME")} :{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, display: "inline" }}
                  >
                    {notification?.patientName || "-"}
                  </Box>
                </div>

                <div>
                  {ERROR
                    ? i18n.t("common.NON_MEDICAL_CONVERSATION")
                    : `${i18n.t("common.SOAP_NOTE_FOR")} ${notification?.title || ""}`}
                </div>


              </>
            ) : (
              ERROR ? i18n.t("common.NON_MEDICAL_CONVERSATION") : notification.title
            )}
          </Typography>
          {notification.status !== NOTIFICATION_STATUS.IN_PROGRESS &&
            notification.status !== NOTIFICATION_STATUS.QUEUED && (
              <UITooltip title={i18n.t("common.DELETE")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(notification);
                  }}
                  sx={{ mt: "-4px" }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </UITooltip>
            )}
        </Box>

        {(notification.status === NOTIFICATION_STATUS.IN_PROGRESS ||
          notification.status === NOTIFICATION_STATUS.QUEUED) && (
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <ProgressBar
                  progress={notification.progress}
                  customSx={{ flex: 1 }}
                  showSteps={false}
                />
                <Chip
                  label={`${notification.progress}%`}
                  size="small"
                  sx={{
                    fontSize: "12px",
                    height: "20px",
                    background: "transparent",
                    border: "none",
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: "4px",
                }}
              >
                <UITooltip title={notification.message}>
                  <Typography
                    fontSize={11}
                    sx={{
                      fontFamily: "Poppins",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      pr: 1,
                    }}
                  >
                    {notification.message}...
                  </Typography>
                </UITooltip>
                <Typography
                  fontSize={9}
                  sx={{ fontWeight: "400", flexShrink: 0 }}
                >
                  {formatNotificationTimestamp(notification?.addedOn)}
                </Typography>
              </Box>
            </Box>
          )}

        <BackgroundTaskFooter
          notification={notification}
          isDarkTheme={isDarkTheme}
          i18n={i18n}
          onRetry={onRetry}
        />
      </Box>
      <Divider variant="middle" />
    </>
  );
};

const NotificationItem = (props) => {
  const { notification } = props;

  if (notification.type !== "background_task") {
    return <ToastNotification {...props} />;
  }

  return <BackgroundTaskNotification {...props} />;
};

export default NotificationItem;